import { useState } from "react";
import styled from "styled-components";
import { AmplitudeUserProperties } from "@lifeat/shared/amplitude/AmplitudeUserProperties";
import { UserCollection, UserEntity } from "@lifeat/shared/data-models/user/UserEntity";
import { getErrorMessage } from "@lifeat/shared/error/ErrorUtil";
import { StripeBillingPortalDetails } from "@lifeat/shared/stripe/StripeSubscriptionTypes";
import { Routes } from "@lifeat/shared/system/RouteConstants";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { LifeAtSingleSelectDropdown } from "@common/LifeAtSingleSelectDropdown";
import LifeAtMultiLineTextInput from "@common/LifeAtTextInput/LifeAtMultiLineTextInput";
import { useDatabaseContext } from "@contexts/DatabaseContext";
import { useToastListContext } from "@contexts/ToastListContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { functions } from "@firebase-local/firebase";
import { STRIPE_FUNCTIONS } from "@firebase-local/functions/ServerlessEndpoints";
import useOnUserDataLoaded from "@hooks/useOnUserDataLoaded";
import InfoPageTemplate from "@pages/templates/InfoPageTemplate";
import * as Tooltip from "@radix-ui/react-tooltip";
import { logError } from "@service/ConsoleLoggerService";
import { Size } from "@themes/Device";
import Styles from "@themes/Styles";
import { SubmitCancelFeedbackEvent, SubmitCancelReasonEvent } from "@user/AmplitudeProSubsEvents";
import { buildRoute } from "@util/RouteUtil";
import { BrowserUtil } from "@util/index";

const Root = styled.main`
    display: block;
    width: clamp(min(100%, ${Size.tablet}), 50%, ${Size.tablet});
    padding: 40px;
`;

const Title = styled.h1`
    margin-bottom: 20px;
    font-size: 24px;
`;

const Ask = styled.p`
    margin-bottom: 12px;
`;

const ActionButton = styled(LifeAtButtonV2)`
    display: block;
    margin-bottom: 12px;
    width: 200px;
`;

const DiscountCode = styled.div`
    width: 200px;
    height: 37px;
    background: ${({ theme }) => theme.backgroundSecondary};
    color: ${({ theme }) => theme.textColorSecondary};
    border: 1px solid ${({ theme }) => theme.borderPrimary};
    border-radius: ${Styles.borderRadiusSharp};
    padding: 6px 15px;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const TooltipContent = styled(Tooltip.Content)`
    font-size: 13px;
    background: ${({ theme }) => theme.backgroundSpecial};
    border-radius: ${Styles.borderRadiusSharp};
    padding: 8px;
`;

const TooltipArrow = styled(Tooltip.Arrow)`
    fill: ${({ theme }) => theme.backgroundSpecial};
`;

const Warning = styled.h2`
    margin-top: 28px;
    margin-bottom: 20px;
    font-size: 20px;
`;

const SingleSelect = styled(LifeAtSingleSelectDropdown)`
    margin-bottom: 12px;
`;

const FeedbackInput = styled(LifeAtMultiLineTextInput)`
    border: 1px solid ${({ theme }) => theme.borderPrimary};
    border-radius: ${Styles.borderRadiusSharp};
    margin-bottom: 28px;
`;

const ValidationError = styled.p`
    font-size: 13px;
    color: red;
`;

const SUCCESSFUL_CANCELLATION_REQUEST_ALERT_MSG =
    "Your subscription has been successfully cancelled.\n\n" +
    "You will now be redirected to the Stripe billing portal to confirm this change.";
const SUBSCRIPTION_CANCEL_REQUEST_ERROR_MSG =
    "There was an error cancelling your subscription.\n\n" +
    "Please try again. If the issue persists, please contact support.";

const NON_ACTIVE_PRO_USER_REDIRECT_MSG = "You are not an active Pro user. Please click to return to the application.";
const NO_CUSTOMER_ID_ALERT_MSG = "Error: No customer ID found. Please contact support.";

const CANCEL_SUBSCRIPTION_BTN_LABEL = "Cancel Subscription";

const RESUB_DISCOUNT_CODE = "40unsubjke";
const LIFETIME_DEAL_MAILTO_LINK =
    "mailto:contact@lifeat.io?subject=Request%20for%20lifetime%20LifeAt%20purchase&body=Hi%20please%20charge%20me%20the%20%24299%20for%20LTV%20of%20pro%20forever%2C%20and%20confirm%20by%20replying%20with%20an%20email%20when%20done.";

const FEEDBACK_CHARACTER_LIMIT = 100;

export default function CancelSubscriptionPage() {
    const amplitudeService = AmplitudeService.getInstance();

    const db = useDatabaseContext();
    const { user, userData }: UserContextType = useUserContext();
    const { addToastNotification } = useToastListContext();

    const [showDiscountCode, setShowDiscountCode] = useState(false);
    const [cancelReason, setCancelReason] = useState<string | null>(null);
    const [cancelFeedback, setCancelFeedback] = useState("");

    const [isCancelRequestLoading, setIsCancelRequestLoading] = useState(false);

    const handleDiscountClick = () => {
        setShowDiscountCode(true);
        navigator.clipboard.writeText(RESUB_DISCOUNT_CODE);
        addToastNotification("Copied the discount code!", "You can use this code during your next payment.");
    };

    const validationError = (() => {
        if (cancelReason == null) {
            return { cancelReason: "This is a required field." };
        } else if (cancelFeedback.length < FEEDBACK_CHARACTER_LIMIT) {
            return { cancelFeedback: `Please enter more than ${FEEDBACK_CHARACTER_LIMIT} characters.` };
        } else {
            return null;
        }
    })();

    const handleCancelClick = () => {
        if (validationError == null) {
            makeCancelSubscriptionStripeRequest();
        }
    };

    useOnUserDataLoaded((userData: UserEntity) => {
        const proUserInfo = userData.proUserInfo;

        if (proUserInfo?.additionalSubscriptionInfo) {
            setCancelReason(proUserInfo?.additionalSubscriptionInfo?.cancelReason ?? "");
            setCancelFeedback(proUserInfo?.additionalSubscriptionInfo?.cancelFeedback ?? "");
        }

        // NOTE: Because we cancel subscriptions at period end, the user is still considered
        //  a Pro user in database until their subscription period ends.
        //  If the user has already been marked as not Pro, we redirect away from the page.
        //  But, since they may be in the middle of their subscription period, we still end
        //  up showing this page, and they can re-request cancellation of their subscription,
        //  which we treat as a duplicate request on the backend and don't actually do anything.
        if (!proUserInfo?.isProUser) {
            confirm(NON_ACTIVE_PRO_USER_REDIRECT_MSG);
            BrowserUtil.redirectTo(buildRoute(Routes.App));
        }
    });

    const makeCancelSubscriptionStripeRequest = () => {
        setIsCancelRequestLoading(true);

        // Save cancel reason and cancel feedback to DB
        if (user) {
            amplitudeService.setUserProp(AmplitudeUserProperties.SUBSCRIPTION_CANCEL_REASON, cancelReason);
            amplitudeService.logEvent(new SubmitCancelReasonEvent(cancelReason));

            const node = UserCollection.at(user.uid)
                .at("proUserInfo")
                .at("additionalSubscriptionInfo")
                .at("cancelReason").node;
            db.set(node, cancelReason).catch((e) => {
                logError(`Error saving cancelReason [${cancelReason}]: ${getErrorMessage(e)}`);
            });
        } else {
            return;
        }

        if (user) {
            amplitudeService.setUserProp(AmplitudeUserProperties.SUBSCRIPTION_CANCEL_FEEDBACK, cancelFeedback);
            amplitudeService.logEvent(new SubmitCancelFeedbackEvent(cancelFeedback));

            const node = UserCollection.at(user.uid)
                .at("proUserInfo")
                .at("additionalSubscriptionInfo")
                .at("cancelFeedback").node;
            db.set(node, cancelFeedback).catch((e) => {
                logError(`Error saving cancelFeedback [${cancelFeedback}]: ${getErrorMessage(e)}`);
            });
        } else {
            return;
        }

        functions
            .httpsCallable(STRIPE_FUNCTIONS.RequestProSubscriptionCancellation)()
            .then(async () => {
                alert(SUCCESSFUL_CANCELLATION_REQUEST_ALERT_MSG);
                const customerId = userData?.proUserInfo?.stripeInfo?.customerId;
                if (customerId) {
                    const payload: StripeBillingPortalDetails = {
                        customerId,
                    };
                    const res = await functions.httpsCallable(STRIPE_FUNCTIONS.OpenBillingPortal)(payload);
                    BrowserUtil.redirectTo(res.data);
                } else {
                    alert(NO_CUSTOMER_ID_ALERT_MSG);
                }
                setIsCancelRequestLoading(false);
            })
            .catch((e) => {
                alert(SUBSCRIPTION_CANCEL_REQUEST_ERROR_MSG + `\n\nError: ${getErrorMessage(e)}`);
                logError(
                    `Error cancelling subscription (request to STRIPE_FUNCTIONS.RequestProSubscriptionCancellation): ${getErrorMessage(
                        e,
                    )}`,
                );
                setIsCancelRequestLoading(false);
            });
    };

    return (
        <InfoPageTemplate showNavBarOverride>
            <Root>
                <Title>What can we offer you to continue with us?</Title>
                <Ask>Before you go, how about a 25% discount forever!</Ask>
                {showDiscountCode ? (
                    <Tooltip.Root delayDuration={0}>
                        <Tooltip.Trigger asChild>
                            <DiscountCode onClick={handleDiscountClick}>{RESUB_DISCOUNT_CODE}</DiscountCode>
                        </Tooltip.Trigger>
                        <TooltipContent side="right">
                            Please use this code during your next payment.
                            <TooltipArrow />
                        </TooltipContent>
                    </Tooltip.Root>
                ) : (
                    <ActionButton variant="outline" onClick={handleDiscountClick}>
                        ✨ Claim 25% discount
                    </ActionButton>
                )}
                <Ask>Claim a Lifetime deal of $299</Ask>
                <ActionButton variant="outline" href={LIFETIME_DEAL_MAILTO_LINK}>
                    ❤️ Claim Lifetime deal
                </ActionButton>
                <Warning>
                    Important: By cancelling your subscription, you will lose your data associated with your paid plan.
                </Warning>
                <Ask>1. Let us know how we can improve?*</Ask>
                {validationError?.cancelReason && <ValidationError>{validationError.cancelReason}</ValidationError>}
                <SingleSelect
                    options={[
                        { label: "I found another tool that better fits my needs", value: "found_another_tool" },
                        { label: "It is not providing enough value for me", value: "not_enough_value" },
                        { label: "I encountered an issue or bug", value: "encountered_issue" },
                        {
                            label: "It doesn't have a feature or functionality that I need",
                            value: "need_specific_feature",
                        },
                        { label: "I cannot afford it at this time", value: "cost" },
                        { label: "Other", value: "other" },
                    ]}
                    selected={cancelReason}
                    onChange={(value) => setCancelReason(value)}
                />
                <Ask>2. Why do you want to cancel?*</Ask>
                {validationError?.cancelFeedback && <ValidationError>{validationError.cancelFeedback}</ValidationError>}
                <FeedbackInput textValue={cancelFeedback} onInputChange={(value) => setCancelFeedback(value)} />
                <LifeAtButtonV2
                    isDisabled={!!validationError}
                    isLoading={isCancelRequestLoading}
                    overrideTextColor="white"
                    overrideBackgroundColor={Styles.buttonFillBlue}
                    disableHover
                    onClick={handleCancelClick}
                >
                    {CANCEL_SUBSCRIPTION_BTN_LABEL}
                </LifeAtButtonV2>
            </Root>
        </InfoPageTemplate>
    );
}
